import axios from 'axios';
import { Consent, User, VisitedSite } from './models';
import { toPascalCaseProperties } from '@/utils/fn';
import moment from '@/plugins/moment';
import router from '@/router';

const service = axios.create({
		baseURL: '/api/ajax/PortalProfileApi/'
	}),
	consentService = axios.create({
		baseURL: '/api/ajax/ConsentsApi/'
	}),
	gdprService = axios.create({
		baseURL: '/api/ajax/GdprApi/'
	}),
	logoutService = axios.create({
		baseURL: '/api/ajax/LogOutApi/'
	}),
	mobileCodeService = axios.create({
		baseURL: '/api/ajax/MobileCodeApi/'
	}),
	onlineSluttKundeService = axios.create({
		baseURL: '/api/ajax/OnlineSluttKundeApi/'
	}),
	SalonProfileApiService = axios.create({
		baseURL: '/api/ajax/SalonProfileApi/'
	}),
	DepartmentApiService = axios.create({
		baseURL: '/api/ajax/DepartmentApi/'
	});

export default {
	/**
	 * Create a new profile for provided User. Verifying creation with verificationCode and clientIdentity.
	 * @param {Object} params
	 * @param {User} params.user - The user to create a new profile for.
	 * @param {String} params.verificationCode - The verification code (single use code) used for authentication.
	 * @param {String} params.clientIdentity - Client's identity to verify that this is the correct client who's verifying.
	 * @param {Boolean} [params.isVerifiedByPassword=false]
	 */
	async CreateProfile({ user = new User(), verificationCode = '', clientIdentity = '', isVerifiedByPassword = false }) {
		user = toPascalCaseProperties(user);
		if (user.BirthDate instanceof moment) user.BirthDate = user.BirthDate.format('YYYY-MM-DD');
		const result = await axios.post('/api/ajax/PortalProfileApi/CreatePortalProfile', {
			Mobile: user.Mobile,
			Code: verificationCode,
			ClientIdentity: clientIdentity,
			Password: user.Password,
			OnlineSluttKunde: user,
			IsVerifiedByPassword: isVerifiedByPassword,
			IsTpn: false,
			TpnChainId: 0
		});
		if (result.data && [201, 202].includes(result.data.ResponseCode)) {
			return new User(result.data.OnlineSluttKunde);
		}

		return false;
	},

	/**
	 * Delete the profile for the current logge in user.
	 * @returns True/false if deletion was successful.
	 */
	async DeleteProfile() {
		const result = await axios.get('/api/ajax/OnlineSluttKundeApi/DeleteCurrentFixitNoUser');
		return !!(result.data && result.data.ResponseCode === 200);
	},

	/**
	 * Get user's current consents
	 * @return {Consent[]|Boolean} Array of consents, or `false` if request failed.
	 */
	async GetConsents(params) {
		const result = await axios.post('/api/ajax/ConsentsApi/GetConsents', params);
		switch (Object(result.data).ResponseCode) {
			case 200:
			case 204:
				return result.data.ConsentList.map(consent => new Consent(consent));

			default:
				return false;
		}
	},

	/**
	 * Get userTags for the logged in / anonynom user
	 * @param {Object} params
	 */
	async GetUserTags() {
		const result = [];

		// Fetch/build tags
		if (window.CONFIG.user.onlineSluttKundeId > 0) {
			result.push('isLoggedIn');
		} else {
			result.push('isNotLoggedIn');
		}

		return result;
	},

	/**
	 * Get userTags for the logged in / anonynom user
	 * @param {Object} params
	 */
	async GetOnlineSluttKunde() {
		const result = await axios.post('/api/ajax/OnlineSluttKundeApi/GetOnlineSluttKunde', {
			istpn: false,
			tpnChainId: 0
		});

		if (result.data && result.data.ResponseCode === 200) {
			return new User(result.data.OnlineSluttKunde);
		}

		return false;
	},

	/**
	 * Get profile for provided mobile number. Requires verification with single-use code or password.
	 * @param {Object} params
	 */
	async GetProfile({ mobile = '', verificationCode = '', clientIdentity = '', isVerifiedByPassword = false, password = '' }) {
		const result = await axios.post('/api/ajax/PortalProfileApi/GetPortalProfile', {
			Mobile: mobile,
			Code: verificationCode,
			ClientIdentity: clientIdentity,
			IsVerifiedByPassword: isVerifiedByPassword,
			Password: password
		});
		if (result.data && result.data.ResponseCode === 200) {
			return new User(result.data.OnlineSluttKunde);
		} else if (result.data && result.data.ResponseCode === 204) {
			return new User();
		}

		return false;
	},

	/**
	 * Log in user with a mobile number and password.
	 */
	async LoginByPassword({ mobile = '', password = '', rememberMe = false, chainId = 0 }) {
		const result = await axios.post('/api/ajax/PortalProfileApi/LoginByPassword', {
			Mobile: mobile,
			Password: password,
			RememberMe: rememberMe,
			TpnChainId: chainId
		});
		if (result.data && result.data.ResponseCode === 200) {
			return new User(result.data.OnlineSluttKunde);
		}

		return false;
	},

	async LoginByJwt() {
		const result = await axios.post('/api/ajax/PortalProfileApi/LoginByJwt', { withCredentials: true });
		if (result.data && result.data.ResponseCode === 200) {
			return new User(result.data.OnlineSluttKunde);
		}

		return false;
	},

	/**
	 * Log in user with verication code sent to user's mobile.
	 * @param {Object} params
	 * @param {String} params.mobile
	 * @param {String} params.verificationCode
	 * @param {String} params.clientIdentity
	 */
	async LoginByVerificationCode({ mobile = '', verificationCode = '', clientIdentity = '' }) {
		const result = await axios.post('/api/ajax/PortalProfileApi/LoginByMobileCode', {
			Mobile: mobile,
			Code: verificationCode,
			ClientIdentity: clientIdentity
		});
		if (result.data && result.data.ResponseCode === 200) {
			return new User(result.data.OnlineSluttKunde);
		}

		return false;
	},

	/**
	 * Log out current user.
	 */
	async Logout() {
		const result = await axios.post('/api/ajax/LogOutApi/LogOut', {});
		return !!(result.data && result.data.ResponseCode === 200);
	},

	/**
	 * GDPR - Request access to user's registered personal information given by onlineSluttKundeId.
	 * A link to the report is provided and a SMS with verification code is sent to the user's registered mobile.
	 * @param {Promise<Number>} onlineSluttKundeId - The onlineSluttKundeId
	 */
	async RequestPersonalInformation(params) {
		const result = await axios.post('/api/ajax/GdprApi/PrepareGdprUser', params);
		if (result.data && result.data.ResponseCode === 200) {
			return result.data.GdprUrl;
		}

		return null;
	},

	/**
	 * Send verification code (single use code) to provided mobile number.
	 * @param {Object} params
	 * @param {String} params.mobile - The mobile number to send the verification code to.
	 * @returns {Promise<String|Boolean>} ClientIdentity - Used to verify the client along with the verification code.
	 */
	async SendVerificationCode({ mobile = '', siteId = 0, isTpn = false }) {
		const result = await axios.post('/api/ajax/MobileCodeApi/SendMobileCode', {
			Mobile: mobile,
			SiteId: siteId,
			IsTpn: isTpn
		});
		if (result.data && result.data.ResponseCode === 200) {
			return result.data.ClientIdentity;
		}

		return false;
	},

	/**
	 * Update consent for current user.
	 * @param {Consent} consent - The consent to update.
	 * @returns {Promise<Boolean>}
	 */
	async UpdateConsent(consent = new Consent()) {
		let chainId = parseInt(router.history.current.params.chainId || router.history.current.query.chainId);
		const result = await axios.post('/api/ajax/ConsentsApi/UpdateCustomerConsent', {
			...consent,
			istpn: !!chainId,
			tpnChainId: chainId,
			tpnSiteId: 0,
			onlineSluttKundeId: window.CONFIG.user.onlineSluttKundeId
		});
		switch (Object(result.data).ResponseCode) {
			case 200:
				return result.data.BooleanValue;

			default:
				return false;
		}
	},

	/**
	 * Update profile for provided User.
	 * @param {Object} params
	 * @param {User} params.user - The user to update profile for.
	 * @param {Boolean} [params.isVerifiedByPassword=false]
	 */
	async UpdateProfile({ user = new User(), isVerifiedByPassword = false }) {
		user = toPascalCaseProperties(user);
		if (user.BirthDate instanceof moment) user.BirthDate = user.BirthDate.format('YYYY-MM-DD');
		const result = await axios.post('/api/ajax/OnlineSluttKundeApi/UpdateOrCreateOnlineSluttKunde', {
			OnlineSluttKunde: user,
			IsVerifiedByPassword: isVerifiedByPassword,
			IsTpn: false,
			TpnChainId: 0
		});
		if (result.data && [201, 202].includes(result.data.ResponseCode)) {
			return new User(result.data.OnlineSluttKunde);
		}

		return false;
	},

	/**
	 * Verify the client along with the single use code.
	 * @param {Object} params
	 * @param {String} params.mobile - The mobile number the verification code was sent to.
	 * @param {String} params.verificationCode - The verification code (single use code) used for authentication.
	 * @param {String} params.clientIdentity - Client's identity to verify that this is the correct client who's verifying.
	 */
	async VerifyCode({ mobile, verificationCode, clientIdentity }) {
		const result = await axios.post('/api/ajax/MobileCodeApi/VerifyMobileCode', {
			Mobile: mobile,
			Code: verificationCode,
			ClientIdentity: clientIdentity
		});
		return !!(result.data && result.data.ResponseCode === 200);
	},
	async GetBestMatchCustomerByMobileAndChainId(mobile, code, clientIdentity, isVerifiedByPassword, password) {
		const data = {
			Mobile: mobile,
			Code: code,
			ClientIdentity: clientIdentity,
			IsVerifiedByPassword: isVerifiedByPassword,
			Password: password
		};
		const response = await axios.post('/api/ajax/SalonProfileApi/GetBestMatchCustomerByMobileAndChainId', data);
		switch (response.data.ResponseCode) {
			case 200:
				return response.data.Customers;
			default:
				return [];
		}
	},
	async GetLastVisitedSalons() {
		const result = await axios.get('/api/ajax/DepartmentApi/GetLastVisitedSalons');

		switch (Object(result.data).ResponseCode) {
			case 200:
				return result.data.SalonList?.map(salon => new VisitedSite(salon));

			case 204:
				return [];

			default:
				return false;
		}
	}
};
